/**
 * 框架通道价格域 Zod 共享契约（A8 §10.3，dev-docs#1519）。
 *
 * 契约源：dev-docs `02-API契约/A8-运营支付.md` §10.3 + `03-数据字典/65-三方支付通道.md` §9。
 * 本文件只挡请求结构；`currency_rule` 下的币种数（PAYERMAX 单币种）随通道能力声明而定，由服务端按声明判定。
 * 币种须为可定价币种（ISO 4217 现行码中小数位有定义的码，字典 65 §9，dev-docs#1532），不只看三位大写形状，
 * 也不只看是否现行码：`XAU` / `XTS` / `XXX` 等小数位为 N.A. 的码同样拒（`../lib/iso4217-current-currency-codes`）。
 *
 * 字段映射（`payment_channel_prices` → 响应 `prices[]`）：
 *   currency → currency · amount_minor_units（bigint）→ amount_minor_units（十进制串）·
 *   provider_price_ref → provider_price_ref（原生恒 null）· updated_at → updated_at（ISO）。
 */

import { z } from 'zod';
import { isPriceableCurrency } from '../lib/iso4217-current-currency-codes';

/** int8 上界 19 位：先截超长串，再在 refine 里比上界（不让百万位串进 BigInt）。 */
const INT8_MAX = 9223372036854775807n;
const POSITIVE_MONEY_PATTERN = /^[1-9][0-9]*$/;

/** 原生行金额：大于零的 minor units 整数串（字典 65 §9「`MERCHANT_AMOUNT` 必填，大于零」）。 */
const PositiveMoneyStringSchema = z
  .string()
  .regex(POSITIVE_MONEY_PATTERN, 'Must be a positive integer string')
  .max(19, 'too many digits for int8 (max 19)')
  .refine((v) => !POSITIVE_MONEY_PATTERN.test(v) || (v.length <= 19 && BigInt(v) <= INT8_MAX), {
    message: 'exceeds int8 max (9223372036854775807)',
  });

/**
 * 原生 PUT 的单条价格。原生通道恒 `MERCHANT_AMOUNT`：**携带** `provider_price_ref`（含 null）即
 * `VALIDATION_FAILED`，错误定位到该项的 `provider_price_ref`（A8 §10.3）。
 */
const NativeChannelPriceInputSchema = z
  .object({
    // 码表判定已蕴含三位大写：单条 refine，错误只落一条、定位到 `prices.<i>.currency`
    currency: z
      .string()
      .refine(
        isPriceableCurrency,
        'currency must be a priceable currency (current ISO 4217 code with defined minor units)',
      ),
    amount_minor_units: PositiveMoneyStringSchema,
    provider_price_ref: z
      .never({ message: 'provider_price_ref is not accepted for native channel prices' })
      .optional(),
  })
  .strict();

/**
 * PUT /api/v1/product-metadata/:id/channel-prices 的 body：整组替换；空数组 = 该商品在该通道不可售。
 * 同一币种只能出现一次（字典 65 §9 唯一约束 `(product_metadata_id, currency)`）。
 */
export const PutNativeChannelPricesRequestSchema = z
  .object({
    prices: z
      .array(NativeChannelPriceInputSchema)
      .max(200)
      .superRefine((prices, ctx) => {
        const seen = new Set<string>();
        for (const price of prices) {
          if (seen.has(price.currency)) {
            ctx.addIssue({
              code: 'custom',
              message: `duplicate currency: ${price.currency}`,
            });
            return;
          }
          seen.add(price.currency);
        }
      }),
  })
  .strict();
export type PutNativeChannelPricesRequest = z.infer<typeof PutNativeChannelPricesRequestSchema>;

/** 响应 `prices[]` 单项。 */
export type ChannelPriceResponseItem = {
  currency: string;
  amount_minor_units: string;
  provider_price_ref: string | null;
  updated_at: string;
};

/**
 * 商品列表 / 单行响应 `channel_prices_summary` 单项（A8 §1，dev-docs#1520）：框架通道行的只读价格汇总，
 * 与 §10.3 GET `prices[]` 同源（未软删价格行），只取币种与金额。
 */
export type ChannelPriceSummaryItem = {
  currency: string;
  amount_minor_units: string;
};

/** 原生 GET / PUT 的 Response 200 `data`（A8 §10.3）。 */
export type NativeChannelPricesResponse = {
  product_metadata_id: string;
  channel: string;
  prices: ChannelPriceResponseItem[];
};

/** Web 通道价格端点对 `MERCHANT_AMOUNT` 通道的拒绝原因（A8 §10.3，`details.reason`）。 */
export const PRICE_AUTHORITY_MERCHANT_AMOUNT = 'PRICE_AUTHORITY_MERCHANT_AMOUNT' as const;
